/**
 * Route guards.
 *
 * Two, doing two different jobs, and keeping them apart matters:
 *
 *  · `RequireSession` answers "is anyone signed in?" and nothing else.
 *  · `RequireCountry` answers "may THIS user be in THIS country?" — the guard that
 *    makes `/countries/:cc/...` safe to have in the URL at all.
 *
 * ===========================================================================
 * THE CLIENT GUARD IS A COURTESY, NOT THE CONTROL
 * ===========================================================================
 * The API enforces country scope with `@CountryScoped` and answers 403
 * `COUNTRY_SCOPE_VIOLATION`; that is the boundary, and it holds whatever this file
 * does. What the client guard buys is that a CD-scoped pilot who types
 * `/countries/IN/inquiries` gets an honest "you do not have access to India"
 * instead of a shell that renders, fires four requests, and fills with 403s. The
 * distinction is worth stating so nobody later "optimises" the server guard away
 * on the grounds that the client already checks.
 *
 * An unknown country code (`/countries/ZZ`) is refused the same way as one out of
 * scope. It is not a 404-shaped "no such country": telling an unauthorised caller
 * which country codes exist is a small oracle, and the two cases are
 * indistinguishable to this user anyway.
 */

import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { isCountryCode } from '../domain/countries';
import type { CountryCode } from '../domain/countries';
import { useSession } from './session';
import { EntryBackdrop, G1Mark } from '../components/EntryBackdrop';
import { Button } from '../components/ui';

/**
 * The boot state.
 *
 * A brand mark, not a spinner: this renders for exactly as long as
 * `POST /auth/refresh` + `GET /me` take on a reload, and the brief is explicit
 * that designed states beat spinners.
 */
function SessionBooting(): React.JSX.Element {
  return (
    <EntryBackdrop>
      <div className="flex flex-col items-center gap-4" aria-busy="true" aria-live="polite">
        <G1Mark />
        <p className="text-label text-fg-inverse/70">Restoring your session…</p>
      </div>
    </EntryBackdrop>
  );
}

export function RequireSession(): React.JSX.Element {
  const { status } = useSession();
  const location = useLocation();

  if (status === 'loading') return <SessionBooting />;
  if (status === 'anonymous') {
    // Remember where they were headed so the login can return them there.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <Outlet />;
}

/** What a country-scoped screen can rely on. */
export interface CountryRouteContext {
  countryCode: CountryCode;
}

export function RequireCountry(): React.JSX.Element {
  const { cc } = useParams<{ cc: string }>();
  const { accessibleCountries, signOut } = useSession();

  const code = cc ?? '';
  const known = isCountryCode(code);
  const permitted = known && accessibleCountries.includes(code);

  if (permitted) return <Outlet />;

  const others = accessibleCountries.length;
  return (
    <EntryBackdrop>
      <div className="mx-auto flex w-full max-w-[440px] flex-col items-center gap-5 text-center">
        <G1Mark />
        <h1 className="text-h1 text-fg-inverse">You do not have access to this company</h1>
        <p className="text-body13 text-fg-inverse/70">
          {others > 0
            ? 'Choose one of the companies your account can reach, or ask an administrator to widen your access.'
            : 'This account has not been given access to any company. An administrator needs to assign you a country role.'}
        </p>
        <div className="flex items-center gap-3">
          {others > 0 ? (
            <Button variant="ghost-on-dark" onClick={() => window.location.assign('/countries')}>
              Choose a company
            </Button>
          ) : null}
          <Button
            variant="ghost-on-dark"
            onClick={() => {
              void signOut();
            }}
          >
            Log out
          </Button>
        </div>
      </div>
    </EntryBackdrop>
  );
}

/**
 * Read the validated country out of the URL.
 *
 * Throws rather than returning a fallback: reaching this without a valid `:cc`
 * means the route was mounted outside `RequireCountry`, and a silent default
 * would be a component quietly reading another country's data.
 */
export function useCountryCode(): CountryCode {
  const { cc } = useParams<{ cc: string }>();
  if (!isCountryCode(cc)) {
    throw new Error(
      `useCountryCode() was called outside a /countries/:cc route (got ${String(cc)}).`,
    );
  }
  return cc;
}
